import React, { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { ChildContext } from '../../context/ChildContext';
import { currencies } from './currencies';

const MyGraph = ({ data = [], width = 600, height = 300 }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [boxDimensions, setBoxDimensions] = useState({ width: 100, height: 60 });
  const textRef = useRef(null);

  const { dashboardData } = useContext(ChildContext);

  const padding = 40;
  const chartWidth = width - 2 * padding;
  const chartHeight = height - 2 * padding;


  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return currency ? currency.symbol : '$'; // Default to '$' if not found
};

    const currencySymbol = getCurrencySymbol(dashboardData.currencyCode);


  const handleMouseMove = useCallback((event) => {
    if (data.length <= 1) return;

    const svgRect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - svgRect.left - padding;
    const xScale = chartWidth / (data.length - 1);
    const index = Math.round(x / xScale);
    if (index >= 0 && index < data.length) {
      setHoveredPoint({ 
        ...data[index], 
        x: index * xScale + padding, 
        y: chartHeight - (data[index].balance - Math.min(...data.map(d => d.balance))) * (chartHeight / (Math.max(...data.map(d => d.balance)) - Math.min(...data.map(d => d.balance)))) + padding 
      });
    }
  }, [data, chartWidth, chartHeight, padding]);

  const handleMouseLeave = useCallback(() => {
    setHoveredPoint(null);
  }, []);

  useEffect(() => {
    if (textRef.current && hoveredPoint) {
      const textWidth = textRef.current.getBBox().width;
      const textHeight = textRef.current.getBBox().height;
      setBoxDimensions({
        width: Math.max(textWidth + 20, 100),
        height: Math.max(textHeight + 20, 60)
      });
    }
  }, [hoveredPoint]);

  if (data.length === 0) {
    return (
      <svg width={width} height={height}>
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#666">
          No data available
        </text>
      </svg>
    );
  }

  if (data.length === 1) {
    const singlePoint = {
      x: width / 2,
      y: height / 2,
      balance: data[0].balance,
      date: data[0].date,
      reason: data[0].reason
    };

    return (
      <svg width={width} height={height}>
        <line x1={padding} y1={height / 2} x2={width - padding} y2={height / 2} stroke="#e0e0e0" strokeDasharray="5,5" />
        <circle cx={singlePoint.x} cy={singlePoint.y} r="6" fill="#007AFF" />
        <text x={singlePoint.x} y={singlePoint.y - 30} textAnchor="middle" fill="#666" fontSize="12">
          ${singlePoint.balance.toFixed(2)}
        </text>
        <text x={singlePoint.x} y={singlePoint.y - 15} textAnchor="middle" fill="#666" fontSize="12">
          {new Date(singlePoint.date).toLocaleDateString()}
        </text>
        <text x={singlePoint.x} y={singlePoint.y + 20} textAnchor="middle" fill="#666" fontSize="12">
          {singlePoint.reason}
        </text>
        <text x={width / 2} y={height - 10} textAnchor="middle" fill="#666">Date</text>
        <text x={10} y={height / 2} textAnchor="middle" fill="#666" transform={`rotate(-90, 15, ${height / 2})`}>Balance</text>
      </svg>
    );
  }

  const xScale = chartWidth / (data.length - 1);
  const yMin = Math.min(...data.map(d => d.balance));
  const yMax = Math.max(...data.map(d => d.balance));
  const yScale = chartHeight / (yMax - yMin);

  const points = data.map((d, i) => ({
    x: i * xScale + padding,
    y: chartHeight - (d.balance - yMin) * yScale + padding,
  }));

  const linePath = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
  const areaPath = `${linePath} L ${points[points.length - 1].x},${height - padding} L ${padding},${height - padding} Z`;

  return (
    <svg width={width} height={height} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <defs>
        <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(0, 122, 255, 0.5)" />
          <stop offset="100%" stopColor="rgba(0, 122, 255, 0.1)" />
        </linearGradient>
      </defs>
      
      {[...Array(5)].map((_, i) => (
        <line
          key={i}
          x1={padding}
          y1={padding + i * (chartHeight / 4)}
          x2={width - padding}
          y2={padding + i * (chartHeight / 4)}
          stroke="#e0e0e0"
          strokeDasharray="5,5"
        />
      ))}

      <path d={areaPath} fill="url(#areaGradient)" />
      <path d={linePath} fill="none" stroke="#007AFF" strokeWidth="3" />

      {points.map((point, i) => (
        <circle key={i} cx={point.x} cy={point.y} r="4" fill="#007AFF" />
      ))}

      <text x={width / 2} y={height - 10} textAnchor="middle" fill="#666">Date</text>
      <text x={10} y={height / 2} textAnchor="middle" fill="#666" transform={`rotate(-90, 15, ${height / 2})`}>Balance</text>

      {hoveredPoint && (
        <g>
          <line x1={hoveredPoint.x} y1={padding} x2={hoveredPoint.x} y2={height - padding} stroke="#007AFF" strokeDasharray="5,5" />
          <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="6" fill="#007AFF" />
          <rect 
            x={Math.min(hoveredPoint.x + 10, width - padding - boxDimensions.width)}
            y={hoveredPoint.y - boxDimensions.height / 2} 
            width={boxDimensions.width} 
            height={boxDimensions.height} 
            rx="5" 
            fill="white" 
            stroke="#007AFF" 
          />
          <text 
            ref={textRef}
            x={Math.min(hoveredPoint.x + 20, width - padding - boxDimensions.width + 10)}
            y={hoveredPoint.y - 10} 
            fill="#007AFF" 
            fontSize="12" 
            dominantBaseline="middle"
          >
            <tspan x={Math.min(hoveredPoint.x + 20, width - padding - boxDimensions.width + 10)} dy="-0.6em">
              {new Date(hoveredPoint.date).toLocaleDateString()}
            </tspan>
            <tspan x={Math.min(hoveredPoint.x + 20, width - padding - boxDimensions.width + 10)} dy="1.2em" fontWeight="bold">
            {currencySymbol}{hoveredPoint.balance.toFixed(2)}
            </tspan>
            <tspan x={Math.min(hoveredPoint.x + 20, width - padding - boxDimensions.width + 10)} dy="1.2em">
              {hoveredPoint.reason}
            </tspan>
          </text>
        </g>
      )}
    </svg>
  );
};

export default MyGraph;